import React, { useState, useEffect } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import katex from "katex";
import "katex/dist/katex.min.css";

// Load KaTeX for the Formula module
import { Quill } from "react-quill";

// Register KaTeX formula module
const Formula = Quill.import("formats/formula");
Quill.register(Formula, true);

const RequestServicePage = ({ onClose }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [points, setPoints] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");
  const [file, setFile] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    fetchPoints();
    const params = new URLSearchParams(location.search);
    if (params.get("payment_success") === "true") {
      alert("Payment was successful! Fetching updated points...");
      fetchPoints();
    }
  }, [location]);

  const fetchPoints = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        "https://genz-staging.feeltiptop.com/api/payment/get-points",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setPoints(data.Points);
    } catch (error) {
      console.error("Error fetching points:", error);
      setMessage("Failed to load points");
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (value) => {
    setDescription(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Prepare form data with title, description, price, and the file
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("price", price);
      if (file) {
        formData.append("file", file);
      }

      // Send the service request directly to /request-service
      const serviceResponse = await axios.post(
        "https://genz-staging.feeltiptop.com/api/payment/request-service",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setPoints(serviceResponse.data.new_points);
      setMessage("Service request created successfully.");
      setMessageType("success");
      setTitle("");
      setDescription("");
      setPrice("");
      setFile(null);
      setFileBase64(null);
      setTouched(false);
    } catch (error) {
      console.error("Error requesting service:", error);
      setMessage(
        error.response?.data?.message ||
          "Add points to your card before requesting",
      );
      setMessageType("error");
    }
  };
  

  const isValidFileType = (filename) => {
    return /\.(jpg|jpeg|png|gif|tiff|pdf)$/i.test(filename);
  };

  const handleFileChange = (event) => {
    setTouched(true);
    const selectedFile = event.target.files[0];
    console.log("File:", selectedFile);
    if (selectedFile && isValidFileType(selectedFile.name)) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        setFileBase64(e.target.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      alert(
        "Invalid file type. Please select an image file (jpg, jpeg, png, gif, tiff, pdf).",
      );
    }
  };

  const styles =
    "w-auto rounded-2xl border border-gray-400 p-2 focus:ring-2 focus:ring-gray-500";

  // Determine if the submit button should be disabled
  const isSubmitDisabled =
    !title || !description || !price || isNaN(price) || price <= 0;

  return (
    <div className="relative mx-auto max-w-xl rounded-lg bg-white p-6">
      <button
        onClick={onClose}
        className="absolute right-5 top-5 rounded-lg px-3 py-2 text-right font-semibold shadow-sm"
      >
        X
      </button>
      <h2 className="mb-2 text-left text-xl font-semibold">
        Request a Service
      </h2>
      <p className="mb-2 flex text-left text-sm font-normal">
        Available Points:
        <span className="ml-2 font-normal">
          {loading ? "Loading..." : points.toLocaleString()}
        </span>
      </p>
      <hr />
      <form onSubmit={handleSubmit} className="mx-auto flex w-full space-y-5">
        <div className="flex flex-col">
          <div>
            <label htmlFor="title" className="w-full text-left text-black">
              Service Title
            </label>
            <input
              type="text"
              id="title"
              placeholder="Give your request a title, any title."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-2xl border border-gray-400 p-2 focus:ring-2 focus:ring-gray-500"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <style>
            {`
          .ql-toolbar.ql-snow + .ql-container.ql-snow {
            min-height: 10rem;
          }
          .ql-editor::before {
            color: #d1d5db; /* Light gray */
          }
        `}
          </style>
          <label htmlFor="description" className="w-full text-left text-black">
            Description
          </label>
          <ReactQuill
            value={description}
            onChange={handleContentChange}
            modules={{
              toolbar: [
                ["bold", "italic", "underline", "strike"], // Text formatting
                ["blockquote", "code-block"], // Block types
                [{ list: "ordered" }, { list: "bullet" }], // Lists
                ["link", "image", "video"], // Media
                ["formula"], // Math
                [{ header: [1, 2, 3, 4, 5, 6, false] }], // Headers
                [{ color: [] }, { background: [] }], // Colors
                [{ align: [] }], // Align
                ["clean"], // Remove formatting
              ],
            }}
            placeholder="Write your description here with clear information to help us
              understand your needs."
            className="w-full rounded-2xl focus:ring-2 focus:ring-gray-500"
          />
        </div>

        {/* <div className="flex flex-col">
          <label htmlFor="price" className="w-full text-left text-black">
            Additional File Upload
            <p className="text-xs text-gray-500">
              Any files you may want to include.
            </p>
          </label>
          <input
            type="file"
            id="file"
            onChange={(e) => handleFileChange(e)}
            accept="image/jpg, image/jpeg, image/png, image/gif, image/tiff, application/pdf"
            className="w-full rounded-lg border border-gray-400 focus:ring-2 focus:ring-gray-500"
          />
        </div>

         */}
        <div className="flex w-full">
          <label
            htmlFor="file-upload"
            className="flex w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 py-2 text-center"
          >
            <img
              src="/Featured icon.svg"
              alt="Upload Icon"
              className="mb-3 h-10 w-10"
            />
            <span className="text-sm">
              <span className="text-blue-600 hover:underline">
                Click to upload
              </span>{" "}
              or drag and drop
            </span>
            <span className="mt-1 text-xs text-gray-400">
              PDF, PNG, JPG or GIF (max. XX x XX px)
            </span>
            <input
              id="file-upload"
              type="file"
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.docx,.png,.jpg,.jpeg,.gif"
            />
          </label>
        </div>

        {touched && !file && (
          <p className="mt-2 text-center text-sm text-red-500">
            ❌ No valid file attached yet
          </p>
        )}
        {file && (
          <p className="mt-2 text-center text-sm text-green-700">
            ✅ File attached: <span className="font-medium">{file.name}</span>
          </p>
        )}

        <div className="flex flex-col">
          <label htmlFor="price" className="w-full text-left text-black">
            Price
          </label>

          <input
            type="number"
            placeholder="How many points are you willing to pay for this service?"
            value={price}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setPrice(value);
              }
            }}
            className={styles}
            step="1"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className={`rounded-2xl px-8 py-2 font-bold text-white transition ${
              isSubmitDisabled
                ? "bg-gray-400"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            disabled={isSubmitDisabled}
          >
            Submit
          </button>
        </div>
      </form>
      {message && (
        <div
          className={`mt-4 rounded-lg p-4 ${
            messageType === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
};

export default RequestServicePage;
